import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, ElementRef, EventEmitter,
    HostListener, Inject, Output, TemplateRef, ViewRef,
} from '@angular/core';
import { ExtendFormGroupDirective } from 'app/utils/validator/extend-form-group.directive';
import { OverlayContainer } from '@angular/cdk/overlay';
import { fromEvent, Subscription, first } from 'rxjs';

declare const Zone;

@Component({
    selector: 'i360-inline',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <ng-template [ngTemplateOutlet]="editing ? editTemplate : valueTemplate"></ng-template>
`,
})
export class InlineComponent {
    editing = false;
    @ContentChild(ExtendFormGroupDirective, {static: true})
    set extendFormGroup(extendFormGroup: ExtendFormGroupDirective) {
        if (extendFormGroup) {
            this.onSubmit = extendFormGroup.i360Submit.pipe(first())
                .subscribe(() => this.done());
            setTimeout(() => extendFormGroup.focusOnDirective());
        }
    }
    @ContentChild('valueTemplate', {static: true}) valueTemplate: TemplateRef<any>;
    @ContentChild('editTemplate', {static: true}) editTemplate: TemplateRef<any>;
    @Output() onBegin = new EventEmitter();
    @Output() onDone = new EventEmitter();
    @Output() afterBegin = new EventEmitter();
    onSubmit = Subscription.EMPTY;
    onCancelOrBlur = Subscription.EMPTY;
    handleErrorZone;
    constructor(private element: ElementRef,
                private overlay: OverlayContainer,
                @Inject(ChangeDetectorRef) private detector: ViewRef) {}
    @HostListener('click', ['$event']) onClick(event): void|false {
        if (this.editing) {
            event.stopPropagation();
            return false;
        }
    }

    begin() {
        this.onBegin.emit();
        this._begin();
    }
    _begin() {
        this.setEditing(true);
        this.onCancelOrBlur = fromEvent(document, 'mouseup')
            .subscribe((e: MouseEvent) => this._cancelOnBlurListener(e));
        this.afterBegin.emit();
    }

    cancel() {
        this.setEditing(false);
        this.onCancelOrBlur.unsubscribe();
        this.onSubmit.unsubscribe();
    }
    done() {
        this.cancel();
        // can not exit zone, so have to reuse it because of calling detectChanges in this zone
        // new submit button appears in this zone
        // looks line angular will not call detectChanges itself until this zone has tasks
        (this.handleErrorZone || (this.handleErrorZone = Zone.current.fork({
            onHandleError: (parentZoneDelegate, currentZone, targetZone, error2) => {
                if (!this.detector.destroyed) {
                    // event listener should be set in angular zone
                    this._begin();
                    // change detection will not fire automatically
                    this.detector.markForCheck();
                }
                parentZoneDelegate.handleError(targetZone, error2);
            },
        }))).run(() => this.onDone.emit());
    }

    private _cancelOnBlurListener(event: MouseEvent) {
        const target = <Node>event.target;
        const inComponent = this.element.nativeElement.contains(target);
        const inOverlay = this.overlay.getContainerElement().contains(target);

        if (!inComponent && !inOverlay) {
            this.cancel();
        }
    }
    private setEditing(value) {
        this.editing = value;
        if (!this.detector.destroyed) {
            this.detector.detectChanges();
        }
    }
}
